import { useApiQuery } from '../useApiQuery';
import { operationsClient, apiGet } from '@/lib/api';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { schedulerKeys } from '@/lib/queryKeys';
import type { SchedulerTechnician } from '@/types/scheduler';

/**
 * Fetches the list of technicians from the scheduler microservice.
 *
 * `includeInactive` opts into deactivated techs (roster table, so they stay
 * visible + reactivatable) under a separate query key — assignment/dispatch
 * callers keep the active-only default cache untouched.
 *
 * `enabled` defers the fetch for on-demand consumers (e.g. the route-assign
 * dialog only needs the roster while it is open).
 */
export function useSchedulerTechnicians(opts?: { includeInactive?: boolean; enabled?: boolean }) {
  const includeInactive = opts?.includeInactive ?? false;
  return useApiQuery<SchedulerTechnician[]>({
    queryKey: includeInactive
      ? [...schedulerKeys.technicians(), 'all']
      : schedulerKeys.technicians(),
    queryFn: () =>
      apiGet<SchedulerTechnician[]>(
        operationsClient,
        `${schedulerEndpoints.technicians()}${includeInactive ? '?includeInactive=true' : ''}`,
      ),
    enabled: opts?.enabled ?? true,
    showErrorToast: false,
  });
}
